import React from "react";
import { CRMTableSheet, TCRMTableColumn } from "../../Simple/CRMTableSheet/CRMTableSheet";
import { WeightBold } from "../../WeightBold/WeightBold";
import { Background } from "../../BuildingBlocks/Background";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import Button from "../../OldFront/Simple/ButtonComponent/Button";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { trimWithEllipsis } from "../../../../../shared/src/utilsByDomain/string/trimWithEllipsis";
import { toDateFormat1 } from "../../../../../shared/src/dateTime";
import * as Party2 from "../../../../../domain/models/Party2";

type TCRMPartyTableProps = {
    parties: Array<Party2.T>;
    showLoadMore: boolean;
    onLoadMore: () => void;
    onPartyClick: (party: Party2.T) => void;
};

const partyNames = (party: Party2.T): string => {
    const names = party.users
        .map((user) => `${user.first_name} ${user.last_name}`.trim())
        .filter((name) => name !== "");
    return names.length > 0 ? names.join(", ") : "—";
};

const partyEmail = (party: Party2.T): string => {
    const withEmail = party.users.find((user) => user.email !== null && user.email !== "");
    return withEmail?.email ?? "—";
};

const partyPhone = (party: Party2.T): string => {
    const phones = party.users.flatMap((user) => user.phone_numbers);
    const chosen =
        phones.find((phone) => phone.primary_number && phone.phone_number !== null && phone.phone_number !== "")
        ?? phones.find((phone) => phone.phone_number !== null && phone.phone_number !== "");
    return chosen?.phone_number ?? "—";
};

const partyAdded = (party: Party2.T): string =>
    party.created_at !== "" ? toDateFormat1(party.created_at) : "—";

export const CRMPartyTable = (props: TCRMPartyTableProps): JSX.Element => {
    const columns: Array<TCRMTableColumn<Party2.T>> = [
        {
            label: "Name(s)",
            width: "2.5fr",
            render: (party) => <WeightBold>{trimWithEllipsis(partyNames(party), 45)}</WeightBold>,
        },
        {
            label: "Email",
            width: "2fr",
            render: (party) => trimWithEllipsis(partyEmail(party), 34),
        },
        {
            label: "Phone",
            width: "1.5fr",
            render: (party) => partyPhone(party),
        },
        {
            label: "Added",
            width: "1fr",
            render: (party) => partyAdded(party),
        },
    ];

    return (
        <CRMTableSheet
            columns={columns}
            sections={[{ rows: props.parties }]}
            getRowKey={(party) => party.id}
            onRowClick={props.onPartyClick}
            emptyMessage="No parties to show."
            footer={props.showLoadMore
                ? <Background padding={`${CRMSpacing.MEDIUM} 0px`}>
                    <SpacingRow justifyContent="center">
                        <Button
                            label="Load more results..."
                            type="frog"
                            fullWidth={false}
                            onClick={props.onLoadMore}
                        />
                    </SpacingRow>
                </Background>
                : undefined}
        />
    );
};
